// =====================================================================
// src/modulos/hospedes.js — Hóspedes: quem está em cada reserva
//
// Duas abas:
//   1. POR RESERVA — as confirmadas de ontem a 14 dias adiante, cada uma
//      com a sua gente e a lotação (2/3). Cadastrar (com a busca de
//      estadias anteriores preenchendo o formulário), editar, tornar
//      titular, remover (antes da chegada).
//   2. BUSCAR PESSOA — estadias anteriores por documento ou nome.
// Tudo que é regra está em 0016; quem só VÊ enxerga sem botões. A camareira
// nunca chega aqui (não tem o módulo — invariante 1).
// =====================================================================
import { hojeDe } from '../lib/contexto.js';
import { el, icone, abrirModal, fecharModal, aviso, confirmar, enquantoAtualiza } from '../ui.js';
import { dataISO, somarDias, FASES, periodo } from '../lib/reservas.js';
import {
  reservasComGente, filtrarReservasComGente, validarHospede, tipoSugerido, tipoAoDigitar, preencherDeRepetido, rotuloRepetido,
  acoesDoHospede, formatarDocumento, idade, TIPOS_DOCUMENTO, digitos, separarNome, nomeCompleto, dadosDaLinha,
  CAMPOS_FNRH, temFicha, fichaDoHospede, argumentosDoHospede, errosDaFicha, fichaParaSalvar, MAX_GENERO_DESCRICAO, textoAntigo, linhaDeEndereco,
  resumoDeHospedes, faltaNaFicha, fichaCompleta, COLUNAS_HOSPEDES, linhasDeHospedes,
} from '../lib/hospedes.js';
import { paraPdf, paraPlanilha, temPdf, temPlanilha, nomeDoArquivo } from '../lib/exportar.js';
import { rotuloFaixa, resumoViagem, MOTIVOS_VIAGEM, MEIOS_TRANSPORTE, SEXOS, RACAS, DEFICIENCIAS, TIPOS_DEFICIENCIA } from '../lib/precheckin.js';
import { seletorPais, blocoResidencia } from './endereco.js';
import { carregarMunicipios, municipio } from '../lib/ibge.js';

export function montar(cont, { auth, contexto, agora = () => Date.now() }) {
  const nivelDe = ch => (contexto.modulos.find(m => m.chave === ch) || {}).nivel;
  const podeEditar = nivelDe('hospedes') === 'editar';
  const sistema = !!(contexto.usuario && contexto.usuario.sistema);
  let vivo = true;
  let reservas = [], clientes = [];
  let coletaRaca = false;   // 0064: módulo FNRH contratado pelo hotel
  let clienteSel = sistema ? null : contexto.cliente.id;
  // `faixa` é o card do topo que está aceso (hospedada · chega · sai).
  // Vazio = todos, que é como a tela abre.
  let filtro = { aba: 'reservas', texto: '', faixa: '' };
  const hoje = () => hojeDe(contexto, agora());   // o dia do hotel (0020), não o do aparelho
  const pc = () => (sistema ? { p_cliente: clienteSel } : {});

  async function carregar() {
    if (sistema) { clientes = await auth.ler('clientes', 'select=id,slug,nome&order=nome'); if (!clienteSel && clientes.length) clienteSel = clientes[0].id; }
    if (!clienteSel) { reservas = []; return; }
    const de = somarDias(hoje(), -1), ate = somarDias(hoje(), 14);
    coletaRaca = await auth.rpc('coleta_raca', { ...pc() }).then(Boolean, () => false);
    reservas = await auth.ler('reservas', `select=id,unidade_id,imobiliaria_id,check_in,check_out,checkout_dia,situacao,unidades(numero,capacidade),imobiliarias(nome),chegada_prevista,placa_veiculo,hospedes(id,nome,documento,tipo_documento,data_nascimento,telefone,email,principal,chegada_em,nacionalidade,profissao,endereco,cidade,uf,pais,cep,nacionalidade_pais,residencia_pais,municipio_id,logradouro,numero_endereco,procedencia,destino,motivo_viagem,meio_transporte,nome_social,sexo,bairro,complemento,legalmente_incapaz,raca,genero_descricao,deficiencia,tipo_deficiencia)&cliente_id=eq.${clienteSel}&situacao=eq.confirmada&check_out=gte.${de}&check_in=lte.${ate}&order=check_in`);
    // 0070: o nome do município (a tabela guarda o código; a lista do IBGE
    // mora num arquivo estático — baixado só se alguém tem município).
    // Sem a lista, a linha mostra a UF: nada quebra.
    const gente = reservas.flatMap(r => r.hospedes || []);
    if (gente.some(h => h.municipio_id)) {
      const idx = await carregarMunicipios().catch(() => null);
      for (const h of gente) { const m = municipio(idx, h.municipio_id); if (m) h.municipio = m.nome; }
    }
  }
  // enquantoAtualiza: o <html> conta os ciclos, e a prova espera o contador
  // crescer em vez de adivinhar que a tela ja se refez (ui.js).
  function recarregar() {
    return enquantoAtualiza(async () => {
      try { await carregar(); } catch (e) { aviso(e.message, 'erro'); return; }
      desenhar();
    });
  }

  function desenhar() {
    if (!vivo) return;
    cont.replaceChildren();
    cont.dataset.tela = 'hospedes';
    cont.appendChild(el('div', { class: 'usu-filtros' },
      sistema ? el('select', { class: 'input', id: 'hp-cliente', style: { width: 'auto' }, onchange: ev => { clienteSel = ev.target.value; recarregar(); } },
        clientes.map(c => el('option', { value: c.id, selected: c.id === clienteSel }, c.nome))) : null,
      el('div', { style: { display: 'flex', gap: '6px' }, id: 'hp-abas' },
        el('button', { type: 'button', class: 'btn btn-sm ' + (filtro.aba === 'reservas' ? 'btn-primary' : 'btn-secondary'), dataset: { aba: 'reservas' }, onclick: () => { filtro.aba = 'reservas'; desenhar(); } }, 'Por reserva'),
        el('button', { type: 'button', class: 'btn btn-sm ' + (filtro.aba === 'buscar' ? 'btn-primary' : 'btn-secondary'), dataset: { aba: 'buscar' }, onclick: () => { filtro.aba = 'buscar'; desenhar(); } }, 'Buscar pessoa')),
      filtro.aba === 'reservas' ? el('input', { type: 'search', class: 'input', id: 'hp-filtro', placeholder: 'Quarto, nome ou documento', value: filtro.texto, oninput: ev => { filtro.texto = ev.target.value; desenharReservas(); } }) : null));
    if (filtro.aba === 'buscar') { desenharBusca(); return; }
    cont.appendChild(topo());
    cont.appendChild(el('div', { id: 'hp-lista' }));
    desenharReservas();
  }

  // ── o topo: a contagem e os três cards ─────────────────────────────
  // Cópia do card de plataforma do `renderHospedes` do Monteiro de Moraes,
  // com o eixo trocado por decisão do Hugo (16/09): no hotel a pergunta da
  // manhã é quem está dentro, quem chega e quem sai. A classe `.card-resumo`
  // é o estilo dele, portado para o index.html; as cores vêm de
  // FAIXAS_HOSPEDES, que são as das badges que a lista já usa.
  function topo() {
    const comGente = reservasComGente(reservas, hoje());
    const gente = comGente.flatMap(r => r.gente || []);
    const pendentes = gente.filter(h => !fichaCompleta(h, coletaRaca)).length;
    const faixas = resumoDeHospedes(comGente, coletaRaca);
    return el('div', { id: 'hp-topo' },
      el('div', { class: 'usu-email', id: 'hp-contagem', style: { marginBottom: '12px' } },
        el('strong', null, String(gente.length)), ' cadastrados · ',
        el('strong', { style: { color: pendentes > 0 ? '#dc2626' : '#059669' }, dataset: { pendentes: String(pendentes) } }, String(pendentes)),
        ' com ficha FNRH pendente'),
      el('div', { class: 'grade-resumo', id: 'hp-cards' }, faixas.map(cardFaixa)));
  }

  function cardFaixa(f) {
    const aceso = filtro.faixa === f.chave;
    const alternar = () => { filtro.faixa = aceso ? '' : f.chave; desenhar(); };
    return el('div', {
      class: 'card card-resumo', style: { '--c': f.cor, '--bg': f.fundo },
      dataset: { faixa: f.chave, pessoas: String(f.pessoas), reservas: String(f.reservas), pendentes: String(f.pendentes), aceso: String(aceso) },
    },
      el('div', { class: 'cr-topo' },
        el('div', { class: 'cr-ico' }, icone(f.icone)),
        el('div', null,
          el('div', { class: 'cr-titulo' }, f.rotulo),
          // aqui o MdM põe o faturado da plataforma. INVARIANTE 2: valor de
          // reserva não aparece para perfil de hotel. Entra a contagem de
          // reservas, que é o que a recepção conta em voz alta.
          el('div', { class: 'cr-sub' }, `${f.reservas} reserva${f.reservas === 1 ? '' : 's'}`))),
      el('div', { class: 'cr-numeros' },
        el('div', { class: 'cr-num' }, el('b', null, String(f.pessoas)), el('span', null, 'pessoas')),
        el('div', { class: 'cr-num ' + (f.pendentes > 0 ? 'cr-alerta' : 'cr-ok') },
          el('b', null, String(f.pendentes)), el('span', null, 'ficha pendente'))),
      el('button', {
        type: 'button', class: 'btn btn-principal', dataset: { verFaixa: f.chave },
        onclick: alternar,
      }, icone('ic-eye'), aceso ? 'Mostrar todas' : 'Ver só estas'),
      (temPdf() || temPlanilha()) && f.pessoas
        ? el('div', { class: 'cr-baixar' },
          temPdf() ? el('button', { type: 'button', class: 'btn btn-secondary btn-sm', dataset: { pdfFaixa: f.chave }, title: `PDF de ${f.rotulo}`, onclick: () => baixarPdf(f) }, icone('ic-baixar'), 'PDF') : null,
          temPlanilha() ? el('button', { type: 'button', class: 'btn btn-secondary btn-sm', dataset: { planilhaFaixa: f.chave }, title: `Planilha de ${f.rotulo}`, onclick: () => baixarPlanilha(f) }, icone('ic-baixar'), 'Planilha') : null)
        : null);
  }

  // ── exportar ───────────────────────────────────────────────────────
  // Mesmo caminho da Auditoria: jsPDF + autoTable e SheetJS, de /vendor/,
  // pelo src/lib/exportar.js — que já é cópia do padrão do MdM.
  const hotel = () => (contexto.cliente && contexto.cliente.nome) || 'Hotel';
  const slug = () => (contexto.cliente && contexto.cliente.slug) || 'hotel';
  const daFaixa = f => reservasComGente(reservas, hoje()).filter(r => r.fase === f.chave);
  function baixarPdf(f) {
    try {
      const linhas = linhasDeHospedes(daFaixa(f), hoje(), coletaRaca);
      paraPdf({
        titulo: f.rotulo, hotel: hotel(),
        subtitulo: `${linhas.length} hóspede(s) · gerado em ${new Date().toLocaleDateString('pt-BR')}`,
        colunas: COLUNAS_HOSPEDES, linhas,
        arquivo: nomeDoArquivo('hospedes', [slug(), f.chave, hoje()], 'pdf'),
      });
    } catch (e) { aviso(e.message, 'erro'); }
  }
  function baixarPlanilha(f) {
    try {
      paraPlanilha({
        aba: f.rotulo, colunas: COLUNAS_HOSPEDES, linhas: linhasDeHospedes(daFaixa(f), hoje(), coletaRaca),
        larguras: [9, 18, 28, 8, 22, 12, 16, 26, 14],
        arquivo: nomeDoArquivo('hospedes', [slug(), f.chave, hoje()], 'xlsx'),
      });
    } catch (e) { aviso(e.message, 'erro'); }
  }

  // ── 1. por reserva ─────────────────────────────────────────────────
  function desenharReservas() {
    const alvo = cont.querySelector('#hp-lista'); if (!alvo) return;
    alvo.replaceChildren();
    let lista = filtrarReservasComGente(reservasComGente(reservas, hoje()), filtro.texto);
    if (filtro.faixa) lista = lista.filter(r => r.fase === filtro.faixa);
    if (!lista.length) {
      alvo.appendChild(el('p', { class: 'vazio', id: 'hp-vazio' },
        filtro.faixa ? 'Nenhuma reserva nesta faixa com esse filtro.'
          : reservas.length ? 'Nada com esse filtro.' : 'Nenhuma reserva confirmada de ontem a 14 dias adiante.'));
      return;
    }
    for (const r of lista) {
      const f = FASES[r.fase];
      alvo.appendChild(el('div', { class: 'card', style: { padding: '12px 14px', marginBottom: '10px' }, dataset: { reserva: r.id, lotacao: r.lotacao } },
        el('div', { style: { display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' } },
          el('span', { class: 'usu-nome', style: { fontSize: '16px' } }, 'Quarto ' + r.quarto),
          el('span', { class: 'badge ' + f.classe }, f.rotulo),
          el('span', { class: 'usu-email' }, periodo(r, hoje())),
          r.imobiliarias ? el('span', { class: 'usu-email' }, r.imobiliarias.nome) : null,
          el('span', { class: 'badge ' + (r.cheia ? 'badge-yellow' : r.semGente ? 'badge-red' : 'badge-gray'), dataset: { lotacao: r.id } }, `${r.lotacao} pessoas`),
          // 0021/0022: o que o hóspede disse da chegada no pré-check-in
          (r.chegada_prevista || r.placa_veiculo) ? el('span', { class: 'usu-email', dataset: { chegada: r.chegada_prevista || '' } },
            [r.chegada_prevista ? `chega ${rotuloFaixa(r.chegada_prevista).toLowerCase()}` : null, r.placa_veiculo ? `placa ${r.placa_veiculo}` : null].filter(Boolean).join(' · ')) : null,
          el('span', { style: { flex: '1' } }),
          podeEditar && !r.cheia ? el('button', { type: 'button', class: 'btn btn-primary btn-sm', dataset: { novo: r.id }, onclick: () => modalHospede(r, null) }, icone('ic-plus'), 'Hóspede') : null),
        r.semGente ? el('p', { class: 'usu-email', style: { margin: '8px 0 0' } }, 'Ninguém cadastrado ainda.') : el('div', { style: { marginTop: '8px' } }, r.gente.map(h => linhaHospede(r, h)))));
    }
  }
  function linhaHospede(r, h) {
    const acoes = acoesDoHospede(h, { podeEditar });
    const i = idade(h.data_nascimento, hoje());
    return el('div', { class: 'ficha-item', dataset: { hospede: h.id, principal: String(h.principal) }, style: { display: 'flex', alignItems: 'center', gap: '10px', padding: '6px 0', borderTop: '1px solid #f3f4f6' } },
      el('span', { style: { flex: '1' } },
        el('span', { class: 'usu-nome' }, h.nome), h.principal ? el('span', { class: 'badge badge-blue', style: { marginLeft: '6px' } }, 'titular') : null, h.chegada_em ? el('span', { class: 'badge badge-green', style: { marginLeft: '6px' } }, 'chegou') : null,
        // O número do card de cima, pessoa por pessoa: sem isto o "3 ficha
        // pendente" do topo não diz DE QUEM, e ninguém consegue agir.
        // O title conta quais campos faltam — é o que a recepção pergunta.
        fichaCompleta(h, coletaRaca) ? null : el('span', {
          class: 'badge badge-orange', style: { marginLeft: '6px' },
          dataset: { fichaPendente: h.id, faltam: String(faltaNaFicha(h, coletaRaca).length) },
          title: 'Faltam na ficha: ' + faltaNaFicha(h, coletaRaca).join(', '),
        }, 'ficha pendente'),
        el('div', { class: 'usu-email' }, [h.documento ? `${h.tipo_documento || 'Doc'} ${formatarDocumento(h.documento, h.tipo_documento)}` : 'sem documento', i !== null ? `${i} anos` : null, h.telefone, h.email].filter(Boolean).join(' · ')),
        // 0021: os campos da FNRH que vieram do pré-check-in (só leitura aqui; o módulo fnrh edita)
        resumoViagem(h) ? el('div', { class: 'usu-email', dataset: { viagem: h.id } }, resumoViagem(h)) : null,
        linhaDeEndereco(h) ? el('div', { class: 'usu-email', dataset: { endereco: h.id } }, linhaDeEndereco(h)) : null),
      acoes.includes('titular') ? el('button', { type: 'button', class: 'btn btn-secondary btn-sm', title: 'Tornar titular', onclick: () => tornarTitular(r, h) }, 'Titular') : null,
      acoes.includes('editar') ? el('button', { type: 'button', class: 'btn btn-secondary btn-sm', title: 'Editar', 'aria-label': 'Editar', onclick: () => modalHospede(r, h) }, icone('ic-edit')) : null,
      acoes.includes('remover') ? el('button', { type: 'button', class: 'btn btn-contorno-vermelho btn-sm', title: 'Remover', 'aria-label': 'Remover', onclick: () => remover(r, h) }, icone('ic-trash')) : null);
  }
  function erroModal(msg) { const e = document.getElementById('modal-erro'); if (e) { e.textContent = msg || ''; e.classList.toggle('hidden', !msg); } }

  function modalHospede(r, h) {
    const novo = !h;
    const campos = {};
    const campo = (id, rotulo, attrs = {}) => { const i = el('input', { class: 'input', id, ...attrs }); campos[id] = i; return el('div', { class: 'form-row' }, el('label', { class: 'label', for: id }, rotulo), i); };

    // ── a ficha completa (FNRH) — 0025 ───────────────────────────────
    // Nasce FECHADA, sempre. No balcão com fila, a recepção quer nome,
    // documento, salvar; onze campos a mais empurram isso para baixo da
    // dobra. E enquanto ela não for aberta, `p_editar_fnrh` vai falso e o
    // banco preserva o que o hóspede preencheu no celular — salvar por
    // engano não apaga a ficha dele.
    const ficha = fichaDoHospede(h);
    const veio = !novo && temFicha(h);
    let fnrhTocado = false;   // abriu uma vez = consentiu editar; fechar de novo não desfaz
    const fnrh = {};
    const cFnrh = (chave, rotulo, attrs = {}) => {
      const i = el('input', { class: 'input', id: 'h-f-' + chave, value: ficha[chave] || '', ...attrs });
      fnrh[chave] = i;
      return el('div', { class: 'form-row' }, el('label', { class: 'label', for: 'h-f-' + chave }, rotulo), i);
    };
    const sFnrh = (chave, rotulo, opcoes) => {
      const s = el('select', { class: 'input', id: 'h-f-' + chave },
        el('option', { value: '' }, '—'),
        opcoes.map(([v, t]) => el('option', { value: v, selected: ficha[chave] === v }, t)));
      fnrh[chave] = s;
      return el('div', { class: 'form-row' }, el('label', { class: 'label', for: 'h-f-' + chave }, rotulo), s);
    };
    // 0065: linha que só aparece com a resposta que a pede (Outro → descrição,
    // Sim → tipo). Escondida, o valor é limpo no envio (`fichaParaSalvar`).
    const condicionais = {};
    const condicional = (chave, visivel, linha) => { linha.classList.toggle('hidden', !visivel); linha.dataset.condicional = chave; condicionais[chave] = linha; return linha; };
    // 0070: país, município e endereço — o componente escreve em `ficha`
    const antigo = textoAntigo(h);
    const corpoFnrh = el('div', { class: 'bloco-corpo hidden', id: 'h-ficha-corpo' },
      el('p', { class: 'usu-email', style: { margin: '0 0 12px' } }, 'Tudo opcional. Só é gravado se você abrir este bloco.'),
      // 0062 (módulo FNRH): nome social e sexo, os dois campos da ficha
      // oficial que a 0025 não tinha
      el('div', { style: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' } },
        cFnrh('nome_social', 'Nome social', { placeholder: 'Só se a pessoa usar' }),
        sFnrh('sexo', 'Gênero', SEXOS)),
      // 0065: gênero "Outro" pede a descrição (API da FNRH v2.4)
      condicional('genero_descricao', ficha.sexo === 'OUTRO', cFnrh('genero_descricao', 'Descrição do gênero', { maxlength: String(MAX_GENERO_DESCRICAO) })),
      coletaRaca ? sFnrh('raca', 'Raça/cor (autodeclarada)', RACAS) : null,
      // 0065: deficiência — saúde, a mesma trava da raça; o tipo só com "Sim"
      coletaRaca ? el('div', { style: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' } },
        sFnrh('deficiencia', 'Pessoa com deficiência', DEFICIENCIAS),
        condicional('tipo_deficiencia', ficha.deficiencia === 'SIM', sFnrh('tipo_deficiencia', 'Tipo de deficiência', TIPOS_DEFICIENCIA))) : null,
      el('div', { style: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' } },
        seletorPais(ficha, 'nacionalidade_pais', 'Nacionalidade', { id: 'h-f-nacionalidade_pais', antes: antigo.nacionalidade_pais }),
        cFnrh('profissao', 'Profissão')),
      blocoResidencia(ficha, { id: c => 'h-f-' + c, antigo }),
      el('div', { style: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' } },
        cFnrh('procedencia', 'De onde vem', { placeholder: 'Cidade' }),
        cFnrh('destino', 'Próximo destino', { placeholder: 'Cidade' })),
      el('div', { style: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' } },
        sFnrh('motivo_viagem', 'Motivo da viagem', MOTIVOS_VIAGEM),
        sFnrh('meio_transporte', 'Meio de transporte', MEIOS_TRANSPORTE)),
      // 0062: sim/não — Hugo, 25/09/2026: marcam o hóspede (no link) e a recepção
      (() => { const c = el('input', { type: 'checkbox', id: 'h-f-legalmente_incapaz', checked: !!ficha.legalmente_incapaz }); fnrh.legalmente_incapaz = c;
        return el('label', { class: 'label', for: 'h-f-legalmente_incapaz', style: { display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' } }, c, 'Pessoa legalmente incapaz (sob tutela ou curatela)'); })());
    if (fnrh.sexo) fnrh.sexo.addEventListener('change', () => condicionais.genero_descricao.classList.toggle('hidden', fnrh.sexo.value !== 'OUTRO'));
    if (fnrh.deficiencia) fnrh.deficiencia.addEventListener('change', () => condicionais.tipo_deficiencia.classList.toggle('hidden', fnrh.deficiencia.value !== 'SIM'));
    const seta = el('span', { class: 'usu-email', dataset: { seta: 'ficha' } }, '▸');
    const cabecaFnrh = el('button', {
      type: 'button', class: 'bloco-cabeca', id: 'h-ficha-abrir', 'aria-expanded': 'false', 'aria-controls': 'h-ficha-corpo',
      style: { display: 'flex', alignItems: 'center', gap: '10px', width: '100%', textAlign: 'left', background: 'none', border: 'none', padding: '11px 0', cursor: 'pointer' },
      onclick: () => {
        const abrindo = corpoFnrh.classList.contains('hidden');
        corpoFnrh.classList.toggle('hidden', !abrindo);
        cabecaFnrh.setAttribute('aria-expanded', String(abrindo));
        seta.textContent = abrindo ? '▾' : '▸';
        if (abrindo) fnrhTocado = true;
      },
    }, seta, el('span', { class: 'usu-nome', style: { flex: '1' } }, 'Ficha completa'),
       el('span', { class: 'badge ' + (veio ? 'badge-green' : 'badge-gray'), dataset: { selo: 'ficha' } }, veio ? 'veio do pré-check-in' : 'em branco'));
    const blocoFnrh = el('div', { dataset: { bloco: 'ficha' }, style: { border: '1px solid #e5e7eb', borderRadius: '10px', padding: '0 14px', margin: '4px 0 16px' } }, cabecaFnrh, corpoFnrh);
    // a escolha na lista trava o tipo (tipoAoDigitar, lib/hospedes.js)
    const tipo = el('select', { class: 'input', id: 'h-tipo', onchange: ev => { ev.target.dataset.manual = '1'; } }, el('option', { value: '' }, '—'), TIPOS_DOCUMENTO.map(([v, t]) => el('option', { value: v, selected: !novo && h.tipo_documento === v }, t)));

    // ── OS DOIS CAMPOS DO NOME, E A PRÉVIA QUE EVITA O NOME DOBRADO ──
    // Quem já tem sobrenome gravado volta partido exato (o CHECK da 0041
    // garante o corte). Quem é de antes da 0041 volta com o nome completo
    // no primeiro campo e o segundo vazio — e é aí que mora a armadilha:
    // se a recepcionista digitar "Silva" ao lado de "Maria Silva", o banco
    // grava "Maria Silva Silva", porque `app.nome_completo` CONCATENA, sem
    // perguntar. Não dá para o código resolver isso sozinho sem chutar
    // onde o nome parte. Dá para MOSTRAR: a prévia diz, enquanto ela
    // digita, exatamente o que vai ser gravado.
    const partes = novo ? { nome: '', sobrenome: '' } : separarNome(h);
    const previaTexto = el('span', { id: 'h-previa-texto' });
    const previaAviso = el('span', { id: 'h-previa-aviso', class: 'badge badge-orange hidden', style: { marginLeft: '8px' } }, 'o sobrenome já está no fim do nome');
    const linhaPrevia = el('div', { class: 'usu-email', id: 'h-previa', style: { margin: '-4px 0 12px' } }, previaTexto, previaAviso);
    function previa() {
      const n = campos['h-nome'].value, s = campos['h-sobrenome'].value;
      const completo = nomeCompleto(n, s);
      previaTexto.textContent = completo ? `Vai ser gravado como: ${completo}` : '';
      const sb = String(s || '').trim();
      // Fato exato, não palpite: o que está no campo Nome termina com o
      // que está no campo Sobrenome. Avisa, não bloqueia — "João Silva
      // Silva" existe, e quem decide é quem está olhando o hóspede.
      previaAviso.classList.toggle('hidden', !(sb && String(n || '').trim().endsWith(' ' + sb)));
    }

    const sugestoes = el('div', { id: 'h-sugestoes' });
    let buscaTimer = null;
    async function buscar() {
      // Procura pelo nome COMPLETO: a coluna `nome` do banco guarda o
      // completo (0041), e o `like` da `hospedes_repetidos` é sobre ela.
      // Procurar só pelo primeiro campo acharia menos a cada sobrenome
      // digitado.
      const porNome = nomeCompleto(campos['h-nome'].value, campos['h-sobrenome'].value);
      const t = porNome.length >= 3 ? porNome : (digitos(campos['h-doc'].value).length >= 3 ? campos['h-doc'].value : '');
      sugestoes.replaceChildren();
      if (!t) return;
      let achados = [];
      try { achados = await auth.rpc('hospedes_repetidos', { p_texto: t, ...pc() }); } catch (_) { return; }
      if (!achados.length) return;
      sugestoes.appendChild(el('div', { class: 'usu-grupo', style: { margin: '0 0 4px' } }, 'Já esteve aqui'));
      for (const p of achados.slice(0, 5)) {
        sugestoes.appendChild(el('button', { type: 'button', class: 'btn btn-secondary btn-sm', style: { display: 'block', width: '100%', textAlign: 'left', marginBottom: '4px' }, dataset: { sugestao: p.documento || p.nome }, onclick: () => {
          const v = preencherDeRepetido(p);
          campos['h-nome'].value = v.nome; campos['h-sobrenome'].value = v.sobrenome; campos['h-doc'].value = v.documento; tipo.value = v.tipo_documento; campos['h-nasc'].value = v.data_nascimento; campos['h-tel'].value = v.telefone; campos['h-email'].value = v.email;
          previa();
          sugestoes.replaceChildren();
        } }, el('span', { class: 'usu-nome' }, p.nome), el('div', { class: 'usu-email' }, rotuloRepetido(p, hoje()))));
      }
    }
    const form = el('form', { id: 'form-hospede', onsubmit: async ev => {
      ev.preventDefault();
      const dados = { nome: campos['h-nome'].value, sobrenome: campos['h-sobrenome'].value, documento: campos['h-doc'].value, tipo_documento: tipo.value || tipoSugerido(campos['h-doc'].value) || null, data_nascimento: campos['h-nasc'].value || null, telefone: campos['h-tel'].value, email: campos['h-email'].value };
      const erros = validarHospede(dados, hoje()); if (erros.length) { erroModal(erros.join(' · ')); return; }
      // os campos soltos vêm do DOM; o país e o endereço, do estado que o componente mantém (0070)
      const daFicha = fichaParaSalvar(Object.fromEntries(CAMPOS_FNRH.map(c => [c, fnrh[c] ? fnrh[c].value : (ficha[c] || '')])));
      daFicha.legalmente_incapaz = !!fnrh.legalmente_incapaz.checked;
      if (fnrhTocado) { const ef = errosDaFicha(daFicha); if (ef.length) { erroModal(ef.join(' · ')); return; } }
      try {
        await auth.rpc('salvar_hospede', { ...argumentosDoHospede({
          id: novo ? null : h.id, reserva_id: r.id, dados, ficha: daFicha,
          principal: novo ? false : h.principal, editarFnrh: fnrhTocado,
        }), ...pc() });
      } catch (e) { erroModal(e.message); return; }
      fecharModal(); aviso(novo ? 'Hóspede cadastrado.' : 'Hóspede salvo.'); recarregar();
    } },
      el('h2', { class: 'modal-title' }, (novo ? 'Novo hóspede' : 'Editar hóspede') + ` · quarto ${r.quarto}`),
      el('div', { id: 'modal-erro', class: 'alert alert-error hidden', role: 'alert' }),
      // 0041/0042 + regra permanente do Hugo (19/09/2026): "sempre utilize
      // nome e sobrenome na identificacao de hospedes em qualquer
      // situacao". Dois campos LADO A LADO, o mesmo desenho do link do
      // hóspede (`src/precheckin.js`, `.linha2`) — e os mesmos
      // `autocomplete`, para o teclado do celular oferecer o que ele já
      // guardou.
      el('div', { style: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' } },
        campo('h-nome', 'Nome', { value: partes.nome, autocomplete: 'given-name', oninput: () => { previa(); clearTimeout(buscaTimer); if (novo) buscaTimer = setTimeout(buscar, 250); } }),
        campo('h-sobrenome', 'Sobrenome', { value: partes.sobrenome, autocomplete: 'family-name', oninput: previa })),
      linhaPrevia,
      novo ? sugestoes : null,
      el('div', { style: { display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '10px' } },
        el('div', { class: 'form-row' }, el('label', { class: 'label', for: 'h-tipo' }, 'Documento'), tipo),
        campo('h-doc', 'Número', { value: novo ? '' : formatarDocumento(h.documento, h.tipo_documento), autocomplete: 'off', inputmode: 'numeric', oninput: () => { tipo.value = tipoAoDigitar(campos['h-doc'].value, { escolhidoAMao: tipo.dataset.manual === '1', atual: tipo.value }); clearTimeout(buscaTimer); if (novo) buscaTimer = setTimeout(buscar, 250); } })),
      el('div', { style: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' } },
        campo('h-nasc', 'Nascimento', { type: 'date', value: novo ? '' : (h.data_nascimento || '') }),
        campo('h-tel', 'Telefone', { value: novo ? '' : (h.telefone || ''), inputmode: 'tel' })),
      campo('h-email', 'E-mail', { value: novo ? '' : (h.email || ''), inputmode: 'email', autocapitalize: 'none' }),
      blocoFnrh,
      el('div', { style: { display: 'flex', gap: '10px' } },
        el('button', { type: 'submit', class: 'btn btn-primary', id: 'btn-h-salvar', style: { flex: '1' } }, 'Salvar'),
        el('button', { type: 'button', class: 'btn btn-secondary', style: { flex: '1' }, onclick: fecharModal }, 'Cancelar')));
    abrirModal(form);
    previa();   // ficha antiga já abre mostrando o que vai ser gravado
    campos['h-nome'].focus();
  }
  async function tornarTitular(r, h) {
    // sem `editarFnrh`: trocar o titular não pode encostar na ficha (0025)
    //
    // `dadosDaLinha(h)`, e não `h`: aqui a pessoa vem do BANCO, com o nome
    // completo em `nome`. Mandar assim fazia o banco juntar de novo e
    // gravar "Marina Costa Costa" — um clique, sem formulário, renomeando
    // o hóspede em silêncio. Quem pegou foi a prova de tela em 20/09.
    try { await auth.rpc('salvar_hospede', { ...argumentosDoHospede({ id: h.id, reserva_id: r.id, dados: dadosDaLinha(h), principal: true }), ...pc() }); }
    catch (e) { aviso(e.message, 'erro'); return; }
    aviso(`${h.nome} é o titular.`); recarregar();
  }
  async function remover(r, h) {
    const sim = await confirmar({ titulo: `Remover ${h.nome} da reserva?`, texto: 'Só antes da chegada. Depois do check-in, encerra-se a estadia.', sim: 'Remover', perigoso: true });
    if (!sim) return;
    try { await auth.rpc('remover_hospede', { p_id: h.id, ...pc() }); } catch (e) { aviso(e.message, 'erro'); return; }
    aviso(`${h.nome} removido.`); recarregar();
  }

  // ── 2. buscar pessoa ───────────────────────────────────────────────
  function desenharBusca() {
    const res = el('div', { id: 'hp-busca-res' });
    let timer = null;
    const input = el('input', { type: 'search', class: 'input', id: 'hp-busca', placeholder: 'Documento ou nome (3 letras ou mais)', autofocus: true, oninput: () => { clearTimeout(timer); timer = setTimeout(procurar, 250); } });
    async function procurar() {
      res.replaceChildren();
      if (input.value.trim().length < 3) return;
      let achados = [];
      try { achados = await auth.rpc('hospedes_repetidos', { p_texto: input.value, ...pc() }); } catch (e) { aviso(e.message, 'erro'); return; }
      if (!achados.length) { res.appendChild(el('p', { class: 'vazio', id: 'hp-busca-vazio' }, 'Ninguém com esse documento ou nome esteve aqui.')); return; }
      const tabela = el('table', { class: 'table-cards' },
        el('thead', null, el('tr', null, el('th', null, 'Pessoa'), el('th', null, 'Documento'), el('th', null, 'Contato'), el('th', null, 'Estadias'))),
        el('tbody', null, achados.map(p => el('tr', { dataset: { pessoa: p.documento || p.nome } },
          el('td', null, el('div', { class: 'usu-nome' }, p.nome), p.data_nascimento ? el('div', { class: 'usu-email' }, `${idade(p.data_nascimento, hoje())} anos`) : null),
          el('td', null, p.documento ? `${p.tipo_documento || ''} ${formatarDocumento(p.documento, p.tipo_documento)}` : '—'),
          el('td', null, [p.telefone, p.email].filter(Boolean).join(' · ') || '—'),
          el('td', null, rotuloRepetido(p, hoje()).split(' · ').slice(0, 2).join(' · '))))));
      res.appendChild(tabela);
    }
    cont.appendChild(el('div', { class: 'card', style: { padding: '14px' } }, el('div', { class: 'form-row' }, el('label', { class: 'label', for: 'hp-busca' }, 'Quem já esteve aqui?'), input), res));
    input.focus();
  }

  recarregar();
  return () => { vivo = false; };
}
